/**
 * Повтор входа: первый экран — бой, даже если сервер недоступен (ошибка входа бой не останавливает, `app/session.ts`).
 * Пока игрок не вошёл, прогресс не копится на сервере — поэтому вход повторяется с растущей задержкой и сразу, когда
 * вернулась сеть.
 */
export interface AuthRetryDeps {
  /** Статус входа из стора. */
  status(): "idle" | "pending" | "ready" | "error";
  /** Подписка на изменения стора; возвращает отписку. */
  subscribe(fn: () => void): () => void;
  signIn(): Promise<void>;
  setTimer?(fn: () => void, ms: number): unknown;
  clearTimer?(id: unknown): void;
}

export const AUTH_RETRY_BASE_MS = 2000;
export const AUTH_RETRY_MAX_MS = 30_000;

export interface AuthRetry {
  /** Сеть вернулась: повторить сейчас. */
  online(): void;
  dispose(): void;
}

export function retrySignIn(deps: AuthRetryDeps): AuthRetry {
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  let timer: unknown = null;
  let failures = 0;

  const cancel = (): void => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };
  const attempt = (): void => {
    timer = null;
    if (deps.status() === "ready") return;
    void deps.signIn();
  };
  const check = (): void => {
    const st = deps.status();
    if (st === "ready") {
      failures = 0;
      cancel();
    } else if (st === "error" && timer === null) {
      failures++;
      timer = setTimer(attempt, Math.min(AUTH_RETRY_MAX_MS, AUTH_RETRY_BASE_MS * 2 ** Math.min(failures - 1, 4)));
    }
  };
  const off = deps.subscribe(check);
  check();
  return {
    online() {
      if (deps.status() !== "error") return;
      cancel();
      attempt();
    },
    dispose() {
      off();
      cancel();
    },
  };
}
